import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

type ToastVariant = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastApi {
  toast: (message: string, variant?: ToastVariant) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const VARIANT_CLASSES: Record<ToastVariant, string> = {
  success: 'border-green-500/40 bg-card text-card-foreground',
  error: 'border-destructive/40 bg-card text-card-foreground',
  info: 'border bg-card text-card-foreground',
  warning: 'border-yellow-500/40 bg-card text-card-foreground',
};

function VariantIcon({ variant }: { variant: ToastVariant }) {
  const Icon =
    variant === 'success'
      ? CheckCircle2
      : variant === 'error'
        ? XCircle
        : variant === 'warning'
          ? AlertTriangle
          : Info;
  return <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />;
}

/**
 * Mirrors `apps/admin-ui/src/components/shared/Toast.tsx`'s `useToast()` API
 * (`toast/success/error/info/warning`). P1-T4 shipped the app without a toast
 * provider, so this one is provided here (feature-neutral, mounted once by the
 * auth shell at `main.tsx` → `App` tree). Copy stays verbatim per design §16.3.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, variant: ToastVariant = 'info') => {
      const id = Date.now() + Math.random();
      setItems((current) => [...current, { id, message, variant }]);
      window.setTimeout(() => dismiss(id), 5000);
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      toast,
      success: (message: string) => toast(message, 'success'),
      error: (message: string) => toast(message, 'error'),
      info: (message: string) => toast(message, 'info'),
      warning: (message: string) => toast(message, 'warning'),
    }),
    [toast],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-[80] flex w-full max-w-sm flex-col gap-2"
      >
        {items.map((item) => (
          <div
            key={item.id}
            className={cn(
              'pointer-events-auto flex items-start gap-2 rounded-lg border px-4 py-3 text-sm shadow-md',
              VARIANT_CLASSES[item.variant],
            )}
          >
            <VariantIcon variant={item.variant} />
            <span className="min-w-0 flex-1">{item.message}</span>
            <button
              type="button"
              onClick={() => dismiss(item.id)}
              className="text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Dismiss notification"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used inside <ToastProvider>');
  }
  return context;
}
